import { LBTooltip, LBTooltipContent, LBTooltipTrigger } from '@linearb/buzz'
import { CheckCircle2, Lock } from 'lucide-react'
import useUserInfo from '../hooks/useUserInfo'
import { splitScopes } from '../constants/scopes'
import {
  FEATURE_FLAG_STATUS_LABELS,
  getChangeableStatuses
} from '../constants/featureFlagStatus'
import { RoleScopeBadgeWithTooltip } from './BackofficeScopeBadges'

/**
 * What the signed-in user may turn on or off for an organization.
 *
 * The counterpart to ScopeIndicator, which cannot express this: enablement is no
 * longer a capability scope but a function of the user's Google group role and the
 * flag's lifecycle status, so there is no single scope to show as held or missing.
 */
const FeatureFlagRoleIndicator: React.FC = () => {
  const userInfo = useUserInfo()
  const scopes = userInfo?.scopes ?? []
  const isAdmin = scopes.includes('admin')
  const { roles } = splitScopes(scopes)
  const statuses = getChangeableStatuses(scopes)

  const canEdit = isAdmin || statuses.length > 0

  // Paired so the summary and its tooltip can't drift apart
  const { summary, tooltip } = isAdmin
    ? {
        summary: 'You can edit Feature Flags',
        tooltip: 'Admin access can change any feature flag'
      }
    : statuses.length
      ? {
          summary: `You can edit ${statuses.map(status => FEATURE_FLAG_STATUS_LABELS[status]).join(', ')} flags`,
          tooltip:
            'Flag changes depend on your Google Workspace role and each flag status'
        }
      : {
          summary: "Can't edit Feature Flags",
          tooltip:
            'Ask IT for the right Google Workspace role to change feature flags'
        }

  return (
    <div className='flex min-w-0 items-center justify-end gap-2'>
      <LBTooltip>
        <LBTooltipTrigger asChild>
          <span
            tabIndex={0}
            className='hidden items-center gap-2 whitespace-nowrap text-sm text-muted-foreground md:inline-flex'
          >
            {canEdit ? (
              <CheckCircle2 className='h-4 w-4 text-green-600' />
            ) : (
              <Lock className='h-4 w-4 text-amber-600' />
            )}
            {summary}
          </span>
        </LBTooltipTrigger>
        <LBTooltipContent className='max-w-64 text-xs leading-relaxed'>
          {tooltip}
        </LBTooltipContent>
      </LBTooltip>
      <div className='flex items-center gap-1'>
        {roles.map(role => (
          <RoleScopeBadgeWithTooltip key={role} name={role} />
        ))}
      </div>
    </div>
  )
}

export default FeatureFlagRoleIndicator
